"use client";

import { Button } from "@/components/ui/Button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/DropdownMenu";
import { ChevronDown, ChevronLeft, ChevronRight, Ellipsis } from "lucide-react";
import { useState } from "react";

export type ProfileActionMenuGroup = {
  content: React.ReactNode;
  icon: React.ReactNode;
  label: string;
};

/**
 * Responsive profile action recipe.
 *
 * Wide profiles keep each action group as a labeled menu. Compact profiles consolidate the
 * same groups beneath one icon trigger while preserving their hierarchy.
 */
export function ProfileActionMenus({
  groups,
  label = "Profile actions",
}: {
  groups: ProfileActionMenuGroup[];
  label?: string;
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileGroupLabel, setActiveMobileGroupLabel] = useState<
    string | null
  >(null);
  const singleMobileGroup = groups.length === 1 ? groups[0] : undefined;
  const activeMobileGroup = singleMobileGroup ?? groups.find(
    (group) => group.label === activeMobileGroupLabel,
  );

  if (groups.length === 0) {
    return null;
  }

  return (
    <>
      <div className="profile-actions-compact md:hidden">
        <DropdownMenu
          open={isMobileMenuOpen}
          onOpenChange={(open) => {
            setIsMobileMenuOpen(open);
            if (!open) {
              setActiveMobileGroupLabel(null);
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button aria-label={label} variant="secondary">
              <Ellipsis />
              <span className="hidden md:inline">Actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            collisionPadding={12}
            className="min-w-48"
          >
            {activeMobileGroup
              ? (
                <>
                  {!singleMobileGroup && (
                    <>
                      <DropdownMenuItem
                        aria-label={`Back to ${label} from ${activeMobileGroup.label}`}
                        onSelect={(event) => {
                          event.preventDefault();
                          setActiveMobileGroupLabel(null);
                        }}
                      >
                        <ChevronLeft aria-hidden="true" />
                        {activeMobileGroup.label}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {activeMobileGroup.content}
                </>
              )
              : (
                <>
                  <DropdownMenuLabel>{label}</DropdownMenuLabel>
                  {groups.map((group) => (
                    <DropdownMenuItem
                      aria-haspopup="menu"
                      key={group.label}
                      onSelect={(event) => {
                        event.preventDefault();
                        setActiveMobileGroupLabel(group.label);
                      }}
                    >
                      {group.icon}
                      {group.label}
                      <ChevronRight aria-hidden="true" className="ml-auto" />
                    </DropdownMenuItem>
                  ))}
                </>
              )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {groups.map((group) => (
        <div className="profile-actions-expanded hidden md:block" key={group.label}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>
                {group.label}
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              {group.content}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ))}
    </>
  );
}
